import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { KENDO_INPUTS, KENDO_TEXTBOX } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaSignIn from '@okta/okta-signin-widget';
import oktaConfigFile from '@app/okta.config';
import { AuthService } from '@services/auth/auth.service';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { environment } from '@env/environment';

const DEFAULT_ORIGINAL_URI = window.location.origin;

@Component({
  selector: 'app-login',
  imports: [
    KENDO_INPUTS,
    KENDO_TEXTBOX,
    KENDO_LABEL,
    ButtonsModule,
    RouterLink,
    KENDO_LOADER,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit {
  // Services
  router: Router = inject(Router);
  oktaAuth = inject(OKTA_AUTH);
  authService = inject(AuthService);
  route: ActivatedRoute = inject(ActivatedRoute);

  // Variables
  isLoggedIn: boolean = false;
  showLoginWidget: boolean = true;
  signIn: any;
  hideNavigation: boolean = false;

  constructor() {
    // initialize Okta SignIn Widget
    this.signIn = new OktaSignIn({
      authClient: this.oktaAuth,
      baseUrl: oktaConfigFile.oidc.oktaBaseUrl,
      clientId: oktaConfigFile.oidc.clientId,
      redirectUri: oktaConfigFile.oidc.oktaRedirectUri,
      logo: 'assets/img/MySoftPro-Logo-Okta.png',
      brandName: 'SoftPro',
      colors: {
        brand: '#3791D2',
      },
      i18n: {
        en: {
          'primaryauth.title': 'Sign in to your account',
          'primaryauth.username.placeholder': 'Username/Email',
          'primaryauth.password.placeholder': 'Password',
        },
      },
      authParams: {
        pkce: true,
        issuer: oktaConfigFile.oidc.issuer,
        responseType: 'code',
      },
      useClassicEngine: true,
      //useInteractionCodeFlow: true,
      features: {
        smsRecovery: true,
        callRecovery: true,
        rememberMe: true,
        multiOptionalFactorEnroll: true,
        autoPush: true,
      },
      //relayState: DEFAULT_ORIGINAL_URI,
    });
  }

  public async ngOnInit(): Promise<any> {
    const logoutParam = this.route.snapshot.queryParamMap.get('logout');
    if (logoutParam != null) {
      await this.authService.signOut();
    } else {
      let isLoggedIn = await this.oktaAuth.isAuthenticated();

      if (isLoggedIn) {
        this.showLoginWidget = false;

        // User is logged in.
        this.hideNavigation = true;

        // redirect user to home page.
        await this.router.navigate(['/']);

        return;
      }
    }

    if (this.showLoginWidget) {
      // Set where the user is logging in from
      this.oktaAuth.setOriginalUri(
        `${environment.webUrl}${environment.loginReturnUri}`,
      );

      // Show and setup user login widget
      this.signIn
        .showSignInAndRedirect({ el: '#sign-in-widget' })
        .catch(function (error: any) {
          // This function is invoked with errors the widget cannot recover from:
          // Known errors: CONFIG_ERROR, UNSUPPORTED_BROWSER_ERROR
          console.log(
            'showSignOnAndredirect -- LOGIN: ERROR ------------------>',
            error,
          );
        });

      this.signIn.on('afterRender', async (context: any) => {
        console.log('context.controller', context.controller);

        // 0. If context.Controller is not primary-auth, then return
        if (context.controller !== 'primary-auth') {
          return;
        }

        // 1. Get reference to sign in button, username field, and password field
        const signInButton = document.getElementById(
          'okta-signin-submit',
        ) as HTMLButtonElement;
        const signInContainer = signInButton.closest(
          '.o-form-button-bar',
        ) as HTMLDivElement;
        const nextButton = signInButton.cloneNode() as HTMLButtonElement;
        const passwordField = document.getElementById(
          'okta-signin-password',
        ) as HTMLInputElement;
        const passwordContainer = passwordField.closest(
          '.o-form-fieldset',
        ) as HTMLDivElement;
        const usernameField = document.getElementById(
          'okta-signin-username',
        ) as HTMLInputElement;
        const usernameContainer = usernameField.closest(
          '.o-form-fieldset',
        ) as HTMLDivElement;

        // 2. Hide the password field and sign in button

        passwordContainer.style.display = 'none';
        signInButton.style.display = 'none';

        const loginForm = usernameField.closest('form');

        // 3. Stand up new, temporary buttons for 'Next' to emulate OIE widget flow, so that on Click we perform WebFinger
        nextButton.value = 'Next';
        nextButton.onclick = async (evt) => {
          console.log('Next button clicked-->', usernameField.value);

          // Validate username before disabling default Okta behavior
          if (!this.validateOktaUsername(usernameField.value)) return false;

          console.log('Username is valid....');

          // Disable default Okta behavior
          evt.preventDefault();

          // Get IDP Name
          const idpName = await this.authService.getAuthDomain(
            usernameField.value,
          );

          console.log(
            'IDP Name -->',
            idpName,
            "does it equal 'softpro'?",
            idpName === 'softpro',
          );

          if (idpName !== 'softpro') {
            // const params = new URLSearchParams({
            //   idp: oktaConfigFile.oidc.oktaIdp,
            //   client_id: oktaConfigFile.oidc.clientId,
            //   response_type: 'code',
            //   scope: oktaConfigFile.oidc.scopes.join(' '),
            //   state: 'abc123',
            //   redirect_uri: oktaConfigFile.oidc.oktaRedirectUri,
            // });

            // window.location.href = `${oktaConfigFile.oidc.issuer}/v1/authorize?${params.toString()}`;
            // return;

            await this.oktaAuth.signInWithRedirect({
              idp: oktaConfigFile.oidc.oktaIdp,
              clientId: oktaConfigFile.oidc.clientId,
              responseType: 'code',
              loginHint: usernameField.value,
              scopes: oktaConfigFile.oidc.scopes,
              originalUri: `${environment.webUrl}${environment.loginReturnUri}`,
              redirectUri: oktaConfigFile.oidc.oktaRedirectUri,
              pkce: true,
              state: 'fromLogin',
            });
            return;
          }

          //var redirectUrl = `${oktaConfigFile.oidc.issuer}/v1/authorize?${params.toString()}`;
          //console.log('Redirecting to -->', redirectUrl);
          passwordContainer.style.display = 'block';
          signInButton.style.display = 'block';
          signInContainer.removeChild(nextButton);

          return false;
        };

        signInContainer.appendChild(nextButton);
      });
    }
  }

  validateOktaUsername(userName: string): boolean {
    // Trim input
    const username = userName?.trim();

    if (!username) return false;

    // Currently we only check to see if the username field is empty.
    // If we wanted to check if the username is an email address we could use this.
    // const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-z]{2,}$/;
    // return emailRegex.test(userName.trim());
    return true;
  }

  // Remove Okta Widget
  ngOnDestroy() {
    this.signIn.remove();
  }
}

// Search for URL Parameters to see if a user is being routed to the application to recover password
//var searchParams = new URL(window.location.href).searchParams;
//this.signIn.otp = searchParams.get('otp');
//this.signIn.state = searchParams.get('state');
// this.isAuthenticated = await this.authService.isAuthenticated();
// if (this.isAuthenticated) {
//   location.href = '/';
//   return;
// }

// When navigating to a protected route, the route path is saved as the `originalUri`
// If no `originalUri` has been saved, then redirect back to the app root
//let originalUri = this.oktaAuth.getOriginalUri();

//originalUri = this.oktaAuth.getOriginalUri();
//if (!originalUri || originalUri === DEFAULT_ORIGINAL_URI) {
//this.oktaAuth.setOriginalUri('/login');
//}
